import { IParkingSession } from "types/vehiclePicker.type";
import { DateTime } from "luxon";
import { fetchData } from "./api";

export class ParkingSessionService {
    static isSessionExpired(session: IParkingSession): boolean {
        if (!session.to) return false;
        const parkingToDateTime = DateTime.fromISO(session.to);
        return parkingToDateTime < DateTime.now();
    }

    static isSessionExpiredMoreThan(session: IParkingSession, minutes: number): boolean {
        if (!session.to) return false;
        const parkingToDateTime = DateTime.fromISO(session.to);
        const expirationThreshold = DateTime.now().minus({ minutes });
        return parkingToDateTime < expirationThreshold;
    }

    static isSessionOlderThanDays(session: IParkingSession, days: number): boolean {
        if (!session.dateCreated) return false;
        const dateCreated = DateTime.fromISO(session.dateCreated);
        const ageThreshold = DateTime.now().minus({ days });
        return dateCreated < ageThreshold;
    }

    static shouldShowSession(session: IParkingSession): boolean {
        if (session.isShown !== undefined) {
            return session.isShown;
        }

        const isActive = session.isActive !== false;
        if (!isActive) {
            return false;
        }

        const isExpired = this.isSessionExpired(session);
        const isExpiredMoreThan10Min = this.isSessionExpiredMoreThan(session, 10);

        return !isExpired || (isExpired && !isExpiredMoreThan10Min);
    }

    static shouldDeleteSession(session: IParkingSession): boolean {
        const isOlderThan2Days = this.isSessionOlderThanDays(session, 2);
        if (isOlderThan2Days) {
            return true;
        }

        const isInactive = session.isActive === false;
        const isOlderThan1Hour = session.dateCreated
            ? DateTime.fromISO(session.dateCreated) < DateTime.now().minus({ hours: 1 })
            : false;

        if (isInactive && isOlderThan1Hour) {
            return true;
        }

        return false;
    }

    static async createPendingSession(
        ticketGUID: string,
        licensePlate: string,
        email: string | undefined,
        requestedMinutes: number,
        sectionCode?: string,
        parkMachineCode?: string,
        addParkingSession?: (session: Partial<IParkingSession>) => Promise<number>
    ): Promise<void> {
        if (!addParkingSession) {
            throw new Error("Missing addParkingSession callback");
        }

        const now = DateTime.now();
        const parkingFrom = now.toISO();
        const parkingTo = now.plus({ minutes: requestedMinutes }).toISO();

        await addParkingSession({
            guid: ticketGUID,
            from: parkingFrom,
            to: parkingTo,
            originFrom: parkingFrom,
            boughtMinutes: requestedMinutes,
            ticket: {
                ticketGUID,
                licensePlate,
                sectionCode: sectionCode || null,
                parkMachineCode: parkMachineCode || null,
                acceptedMinutes: requestedMinutes,
                parkingFrom,
                parkingTo,
                dateCreated: now.toISO(),
            } as any,
            maxHour: null,
            acceptedMinutes: requestedMinutes,
            dateCreated: now.toISO(),
            email: email,
            isActive: false, // Neaktívna - čaká na platbu
            isShown: false, // Nezobrazuje sa v UI
        });
    }

    static areSessionsFromSameDay(session1DateTime: DateTime, session2DateTime: DateTime): boolean {
        return (
            session1DateTime.year === session2DateTime.year &&
            session1DateTime.month === session2DateTime.month &&
            session1DateTime.day === session2DateTime.day
        );
    }

    static async activatePendingSession(
        ticketGUID: string,
        parkingFrom: string,
        parkingTo: string,
        ticketData: any,
        dateCreated: string,
        email?: string,
        getAllSessions?: () => Promise<IParkingSession[]>,
        deleteParkingSession?: (indexName: keyof IParkingSession, key: any) => Promise<void>,
        addParkingSession?: (session: Partial<IParkingSession>) => Promise<number>
    ): Promise<void> {
        if (!getAllSessions || !deleteParkingSession || !addParkingSession) {
            throw new Error("Missing required callbacks for session management");
        }

        const sectionsData = await fetchData("StreetParking/sections/1", "GET", "url", {
            sectionCode: ticketData.sectionCode,
        });

        let maxHour = sectionsData?.[0]?.tariffInfo?.maxHour ?? null;
        let totalAcceptedMinutes = ticketData.acceptedMinutes; // Celkové minúty za deň
        let boughtMinutes = ticketData.acceptedMinutes; // Celkové kúpené minúty
        let originFrom = parkingFrom; // Prvá session v sérii
        let originalDateCreated = dateCreated;

        const sessions = await getAllSessions();

        for (const session of sessions) {
            const isSameLicenseAndSection =
                session.ticket.licensePlate === ticketData.licensePlate && session.ticket.sectionCode === ticketData.sectionCode;

            const isPendingSessionSameGUID = session.guid === ticketGUID && session.isActive === false;
            const isPendingSessionSameLicense = isSameLicenseAndSection && session.isActive === false;
            const isPendingSession = isPendingSessionSameGUID || isPendingSessionSameLicense;

            const isActiveSession = session.isActive !== false;

            const isSessionToMerge = isSameLicenseAndSection && isActiveSession;

            if (isPendingSession) {
                await deleteParkingSession("id", session.id);
            } else if (isSessionToMerge) {
                const sessionOriginFrom = DateTime.fromISO(session?.originFrom || session.from);
                const currDateFrom = DateTime.fromISO(parkingFrom);

                originFrom = sessionOriginFrom < currDateFrom ? session?.originFrom || session.from : parkingFrom;

                originalDateCreated = session.dateCreated || dateCreated;

                totalAcceptedMinutes += session.acceptedMinutes || session.ticket.acceptedMinutes || 0;
                boughtMinutes += session.boughtMinutes || 0;

                await deleteParkingSession("id", session.id);
            }
        }

        await addParkingSession({
            guid: ticketGUID,
            from: parkingFrom,
            to: parkingTo,
            originFrom, // Od kedy začala prvá session v sérii
            boughtMinutes, // Celkové kúpené minúty
            ticket: ticketData,
            maxHour: maxHour,
            acceptedMinutes: totalAcceptedMinutes, // Celkové akceptované minúty za deň
            dateCreated: originalDateCreated, // Dátum vytvorenia prvej session
            email: email,
            isActive: true,
            isShown: true,
        });
    }

    static async hideSession(
        session: IParkingSession,
        updateSession?: (session: IParkingSession) => Promise<void>
    ): Promise<void> {
        if (updateSession) {
            await updateSession({ ...session, isShown: false });
        }
    }

    static async deactivateSession(
        session: IParkingSession,
        updateSession?: (session: IParkingSession) => Promise<void>
    ): Promise<void> {
        if (updateSession) {
            await updateSession({ ...session, isActive: false });
        }
    }

    static filterVisibleSessions(sessions: IParkingSession[]): IParkingSession[] {
        return sessions.filter((session) => this.shouldShowSession(session));
    }

    static async cleanupOldSessions(
        getAllSessions: () => Promise<IParkingSession[]>,
        deleteByIndex: (indexName: keyof IParkingSession, key: any) => Promise<void>
    ): Promise<number> {
        const sessions = await getAllSessions();
        let deletedCount = 0;

        for (const session of sessions) {
            if (this.shouldDeleteSession(session)) {
                try {
                    await deleteByIndex("id", session.id);
                    deletedCount++;
                } catch (error) {
                    console.error("Failed to delete session:", session.guid, error);
                }
            }
        }

        return deletedCount;
    }
}
